import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { PreloaderService } from '../../services/preloader.service';
import { startPingRequestBackground } from './pingRequest';
import { joinBaseAndPath } from './requestWithFallback';
import { HubEndpoint } from './signalRHubWithFallback';
import { defaultIsFallbackError, UrlFallback } from './urlFallback';
import { UrlFallbackView } from './urlFallbackView';

@Injectable({
  providedIn: 'root',
})
export class ApiConnectionService {
  /** Один pingRequest-цикл на пару apiProxyHosts+apiPingRequestAddress. */
  private readonly apiUrlFallback = new UrlFallback(
    environment.apiProxyHosts,
    'api',
    environment.apiPingRequestAddress,
    defaultIsFallbackError,
  );

  readonly apiUrlFallbackView = new UrlFallbackView(
    this.apiUrlFallback,
    environment.apiUrlSuffix,
  );

  /** apiClear — тот же хост, что и api, без суффикса `/v2/wg`, поэтому делит с ним один пул. */
  readonly hubUrlFallbackView = new UrlFallbackView(
    this.apiUrlFallback,
    environment.apiClearUrlSuffix,
  );

  /**
   * Один pingRequest-цикл на пару paymentProxyHosts+paymentPingRequestAddress — раньше
   * paymentUrl/paymentBaseUrl/paymentCertUrl/externalPaymentUrl/externalPaymentSbpUrl были
   * пятью независимыми пулами на одном и том же хосте, теперь это один core-пул и пять
   * view с разными суффиксами.
   */
  private readonly paymentUrlFallback = new UrlFallback(
    environment.paymentProxyHosts,
    'payment',
    environment.paymentPingRequestAddress,
    defaultIsFallbackError,
  );

  readonly paymentUrlFallbackView = new UrlFallbackView(
    this.paymentUrlFallback,
    environment.paymentUrlSuffix,
  );
  readonly paymentBaseUrlFallbackView = new UrlFallbackView(
    this.paymentUrlFallback,
    environment.paymentBaseUrlSuffix,
  );
  readonly paymentCertUrlFallbackView = new UrlFallbackView(
    this.paymentUrlFallback,
    environment.paymentCertUrlSuffix,
  );
  readonly externalPaymentUrlFallbackView = new UrlFallbackView(
    this.paymentUrlFallback,
    environment.externalPaymentUrlSuffix,
  );
  readonly externalPaymentSbpUrlFallbackView = new UrlFallbackView(
    this.paymentUrlFallback,
    environment.externalPaymentSbpUrlSuffix,
  );

  constructor(private preloaderService: PreloaderService) {
    // Не очищаем результаты прошлого ping: localStorage общий на origin и разделяется всеми
    // инстансами виджета. Новый инстанс должен сразу работать по последнему централизованному
    // snapshot (stale-while-revalidate), а фоновый ping лишь обновит его; очистка же приводила
    // к тому, что свежесозданная вкладка уничтожала рабочий результат соседа/прошлого запуска.
    startPingRequestBackground(this.apiUrlFallback);
    startPingRequestBackground(this.paymentUrlFallback);
  }

  /**
   * Резолвинг URL для iframe/navigation-сценариев (см. UrlFallbackView.resolveUrl) — ждём
   * фоновый pingRequest, если он как раз идёт, и показываем общую плашку (PreloaderService),
   * чтобы пользователь не остался с URL, указывающим на хост, который прямо сейчас проверяется.
   */
  async resolvePaymentUrl(): Promise<string> {
    await this.paymentUrlFallbackView.waitUntilReadyWithPreloader(
      this.preloaderService,
    );
    return this.paymentUrlFallbackView.resolveUrl();
  }

  async resolvePaymentBasePath(path: string): Promise<string> {
    await this.paymentBaseUrlFallbackView.waitUntilReadyWithPreloader(
      this.preloaderService,
    );
    return this.paymentBaseUrlFallbackView.resolveUrl(path);
  }

  async resolvePaymentCertUrl(query?: string): Promise<string> {
    await this.paymentCertUrlFallbackView.waitUntilReadyWithPreloader(
      this.preloaderService,
    );
    const base = this.paymentCertUrlFallbackView.resolveUrl();
    return query ? `${base}?${query}` : base;
  }

  async resolveExternalPaymentUrl(query: string): Promise<string> {
    await this.externalPaymentUrlFallbackView.waitUntilReadyWithPreloader(
      this.preloaderService,
    );
    const base = this.externalPaymentUrlFallbackView.resolveUrl();
    return `${base}?${query}`;
  }

  async resolveExternalPaymentSbpUrl(query: string): Promise<string> {
    await this.externalPaymentSbpUrlFallbackView.waitUntilReadyWithPreloader(
      this.preloaderService,
    );
    const base = this.externalPaymentSbpUrlFallbackView.resolveUrl();
    return `${base}?${query}`;
  }

  resolveHubPath(hubPath: string): HubEndpoint {
    const target = this.hubUrlFallbackView.tryGetNearestAvailableUrl();
    const base = target?.url ?? environment.apiClear;
    return {
      url: joinBaseAndPath(base, hubPath),
      plannedIndex: target?.index ?? 0,
    };
  }
}
